import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, Loader2, MapPin, RefreshCw, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchStoredJobDetail, fetchStoredJobFilters, safeApiMessage, searchStoredJobs } from '../data/storedJobsApi';

const PAGE_SIZE = 10;
const CHECK_FIT_JOB_KEY = 'jobmatchpro:selected-stored-job-id';
const CHECK_FIT_JOB_SNAPSHOT_KEY = 'jobmatchpro:selected-stored-job-snapshot';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function optionId(option) {
  return String(option?.id || option?.value || '').trim();
}

function optionLabel(option) {
  return String(option?.label || option?.name || option?.title || optionId(option)).trim();
}

function extractOptions(filters, key) {
  const source = filters?.[key] || filters?.filters?.[key] || [];
  return asArray(source).map((option) => ({ id: optionId(option), label: optionLabel(option) })).filter((option) => option.id && option.label);
}

function extractCombinations(filters) {
  const source = filters?.combinations ?? filters?.filters?.combinations;
  if (source === undefined || source === null) return null;
  if (!Array.isArray(source)) return null;
  const normalized = source
    .map((combination) => ({
      roleId: String(combination?.role_id || combination?.roleId || '').trim(),
      locationId: String(combination?.location_id || combination?.locationId || '').trim(),
      count: Number(combination?.count),
    }))
    .filter((combination) => combination.roleId && combination.locationId && Number.isFinite(combination.count));
  if (source.length > 0 && normalized.length === 0) return null;
  return normalized.filter((combination) => combination.count > 0);
}

function optionIds(options) {
  return new Set(options.map((option) => option.id));
}

function hasCombination(combinations, roleId, locationId) {
  return combinations.some((combination) => combination.roleId === roleId && combination.locationId === locationId);
}

function extractJobs(payload) {
  if (Array.isArray(payload)) return payload;
  return asArray(payload?.jobs || payload?.results);
}

function totalPages(payload, count) {
  const pageSize = Number(payload?.page_size || PAGE_SIZE) || PAGE_SIZE;
  const total = Number(payload?.total || payload?.total_count || count) || count;
  return Math.max(1, Math.ceil(total / pageSize));
}

function jobId(job) {
  return String(job?.job_id || job?.id || '').trim();
}

function jobTitle(job) {
  return String(job?.title || job?.job_title || 'Untitled role').trim();
}

function jobCompany(job) {
  return String(job?.company || job?.company_name || 'Company not listed').trim();
}

function jobLocation(job) {
  return String(job?.location || job?.location_label || 'Location not listed').trim();
}

function workArrangement(job) {
  return String(job?.work_arrangement || job?.work_setup || '').trim() || 'Work arrangement unknown';
}

function salaryLabel(job) {
  const direct = String(job?.salary || job?.salary_text || job?.salary_label || job?.salary_range || '').trim();
  if (direct) return direct;
  const currency = String(job?.salary_currency || job?.currency || '').trim();
  const period = String(job?.salary_period || job?.period || '').trim();
  const min = job?.salary_min ?? job?.min_salary;
  const max = job?.salary_max ?? job?.max_salary;
  const hasMin = min !== undefined && min !== null && String(min).trim() !== '';
  const hasMax = max !== undefined && max !== null && String(max).trim() !== '';
  if (!hasMin && !hasMax) return 'Salary not provided';
  const formatter = new Intl.NumberFormat('en', { maximumFractionDigits: 0 });
  const prefix = currency || '';
  const suffix = period ? ` / ${period}` : '';
  if (hasMin && hasMax) return `${prefix}${formatter.format(Number(min))}–${prefix}${formatter.format(Number(max))}${suffix}`;
  return `${prefix}${formatter.format(Number(hasMin ? min : max))}${suffix}`;
}

function contractType(job) {
  return String(job?.contract_type || job?.employment_type || job?.contract || '').trim();
}

function fitMessage(job) {
  const message = String(job?.fit_status_message || job?.fit_message || '').trim();
  return /not[_\s-]*calculated/i.test(message) ? 'Fit not checked' : (message || 'Fit not checked');
}

function thinMessage(payload) {
  return String(payload?.thin_message || payload?.thin_results_message || '').trim();
}

function emptyMessage(payload) {
  return String(payload?.empty_message || payload?.no_results_message || '').trim();
}

function safeStoreSelectedJob(job) {
  const id = jobId(job);
  if (!id || typeof window === 'undefined') return;
  try { window.sessionStorage.setItem(CHECK_FIT_JOB_KEY, id); } catch (_) { return; }
  try {
    window.sessionStorage.setItem(CHECK_FIT_JOB_SNAPSHOT_KEY, JSON.stringify({
      job_id: id,
      title: jobTitle(job),
      company: jobCompany(job),
      location: jobLocation(job),
      fit_status: 'not_calculated',
    }));
  } catch (_) { return; }
}

function ApiNotice({ title, message, onRetry, testid }) {
  return (
    <div className="rounded-2xl border border-[#d8c8ab] bg-[#fff4dc] p-4 text-sm text-[#6f5b2f]" data-testid={testid}>
      <p className="font-medium text-[#22362f]">{title}</p>
      {message && <p className="mt-1 leading-relaxed">{message}</p>}
      {onRetry && (
        <Button type="button" variant="outline" className="mt-3" onClick={onRetry} data-testid={`${testid}-retry-button`}>
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      )}
    </div>
  );
}

function JobCard({ job, selected, onSelect, onCheckFit }) {
  const id = jobId(job);
  return (
    <article className={`jmp-card-interactive relative p-5 ${selected ? 'border-[#10231D] bg-[#FFFDF8] shadow-[0_14px_30px_rgba(16,35,29,0.12)] before:absolute before:left-0 before:top-4 before:h-[calc(100%-2rem)] before:w-[3px] before:bg-[#B7F34A]' : ''}`} data-testid={`stored-job-card-${id}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <button type="button" onClick={() => onSelect(id)} className="min-h-[44px] flex-1 text-left" data-testid={`stored-job-select-${id}`}>
          <p className="font-serif-display text-xl font-medium text-[#172820]" data-testid={`stored-job-title-${id}`}>{jobTitle(job)}</p>
          <p className="mt-1 text-sm text-[#30473e]" data-testid={`stored-job-company-${id}`}>{jobCompany(job)}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs text-[#6f6657]">
            <span className="jmp-chip gap-1" data-testid={`stored-job-location-${id}`}><MapPin className="h-3.5 w-3.5" /> {jobLocation(job)}</span>
            <span className="jmp-chip" data-testid={`stored-job-salary-${id}`}>{salaryLabel(job)}</span>
            <span className="jmp-chip" data-testid={`stored-job-work-arrangement-${id}`}>{workArrangement(job)}</span>
            {contractType(job) && <span className="jmp-chip" data-testid={`stored-job-contract-type-${id}`}>{contractType(job)}</span>}
          </div>
          <div className="mt-4 rounded-xl border border-[#d8c8ab] bg-white/45 p-3 text-sm" data-testid={`stored-job-fit-status-${id}`}>
            <p className="text-xs uppercase tracking-[0.14em] text-[#7b694d]">Fit not checked</p>
            <p className="mt-1 text-[#30473e]">{fitMessage(job)}</p>
          </div>
        </button>
        <Button type="button" onClick={() => onCheckFit(job)} className="btn-primary stored-job-check-fit-cta shrink-0" disabled={!id} data-testid={`check-my-fit-${id}`}>
          Check my fit <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </article>
  );
}

function JobDetail({ job, loading, error, onRetry, onCheckFit }) {
  if (loading) {
    return <div className="rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-5" data-testid="stored-job-detail-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading job details…</div>;
  }
  if (error) {
    return <ApiNotice title="Job detail unavailable" message={safeApiMessage(error)} onRetry={onRetry} testid="stored-job-detail-error" />;
  }
  if (!job) {
    return <div className="rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-5 text-sm text-[#6f6657]" data-testid="stored-job-detail-placeholder">Select a job to review basic details.</div>;
  }
  const id = jobId(job);
  const detailRows = [
    ['Company', jobCompany(job)],
    ['Location', jobLocation(job)],
    ['Salary', salaryLabel(job)],
    ['Work arrangement', workArrangement(job)],
    ...(contractType(job) ? [['Contract type', contractType(job)]] : []),
    ['Fit', 'Fit not checked'],
  ];
  return (
    <aside className="jmp-panel p-5 lg:sticky lg:top-8" data-testid="stored-job-detail-panel">
      <p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Job detail</p>
      <h2 className="mt-2 font-serif-display text-2xl font-medium text-[#172820]" data-testid="stored-job-detail-title">{jobTitle(job)}</h2>
      <div className="mt-5 space-y-3">
        {detailRows.map(([label, value]) => (
          <div key={label} className="rounded-md border border-[#D7C9AF] bg-[#FFFDF8] p-3" data-testid={`stored-job-detail-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <p className="text-[11px] uppercase tracking-[0.14em] text-[#7b694d]">{label}</p>
            <p className="mt-1 text-sm text-[#30473e]">{value}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 rounded-md border border-[#D7C9AF] bg-[#FFFDF8] p-3 text-sm text-[#30473e]" data-testid="stored-job-detail-fit-message">{fitMessage(job)}</p>
      <Button type="button" onClick={() => onCheckFit(job)} className="btn-primary stored-job-check-fit-cta mt-5 w-full" disabled={!id} data-testid="stored-job-detail-check-my-fit">
        Check my fit <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </aside>
  );
}

export default function StoredJobSearchPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(null);
  const [role, setRole] = useState('');
  const [location, setLocation] = useState('');
  const [sort, setSort] = useState('relevance');
  const [page, setPage] = useState(1);
  const [validationError, setValidationError] = useState('');
  const [searchPayload, setSearchPayload] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [detailJob, setDetailJob] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const roleOptions = useMemo(() => extractOptions(filters, 'roles'), [filters]);
  const locationOptions = useMemo(() => extractOptions(filters, 'locations'), [filters]);
  const combinations = useMemo(() => extractCombinations(filters), [filters]);
  const availableRoleOptions = useMemo(() => {
    if (!combinations || !location) return roleOptions;
    const validRoleIds = new Set(combinations.filter((combination) => combination.locationId === location).map((combination) => combination.roleId));
    return roleOptions.filter((option) => validRoleIds.has(option.id));
  }, [combinations, location, roleOptions]);
  const availableLocationOptions = useMemo(() => {
    if (!combinations || !role) return locationOptions;
    const validLocationIds = new Set(combinations.filter((combination) => combination.roleId === role).map((combination) => combination.locationId));
    return locationOptions.filter((option) => validLocationIds.has(option.id));
  }, [combinations, role, locationOptions]);
  const jobs = useMemo(() => extractJobs(searchPayload), [searchPayload]);
  const pages = useMemo(() => totalPages(searchPayload, jobs.length), [searchPayload, jobs.length]);

  const handleRoleChange = useCallback((nextRole) => {
    setRole(nextRole);
    if (combinations && nextRole && location && !hasCombination(combinations, nextRole, location)) {
      setLocation('');
    }
  }, [combinations, location]);

  const handleLocationChange = useCallback((nextLocation) => {
    setLocation(nextLocation);
    if (combinations && nextLocation && role && !hasCombination(combinations, role, nextLocation)) {
      setRole('');
    }
  }, [combinations, role]);

  useEffect(() => {
    const roles = optionIds(roleOptions);
    const locations = optionIds(locationOptions);
    if (role && !roles.has(role)) setRole('');
    if (location && !locations.has(location)) setLocation('');
  }, [location, locationOptions, role, roleOptions]);

  const loadFilters = useCallback(async () => {
    setFiltersLoading(true);
    setFiltersError(null);
    try {
      const data = await fetchStoredJobFilters();
      setFilters(data);
    } catch (error) {
      setFiltersError(error);
    } finally {
      setFiltersLoading(false);
    }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  const runSearch = useCallback(async (nextPage = 1) => {
    if (!role || !location) {
      setValidationError('Choose a role and location from the available filters.');
      return;
    }
    setValidationError('');
    setSearchLoading(true);
    setSearchError(null);
    try {
      const data = await searchStoredJobs({ role, location, page: nextPage, pageSize: PAGE_SIZE, sort });
      setSearchPayload(data);
      setPage(nextPage);
      const firstJob = extractJobs(data)[0];
      setSelectedJobId(firstJob ? jobId(firstJob) : '');
    } catch (error) {
      setSearchError(error);
      setSearchPayload(null);
      setSelectedJobId('');
    } finally {
      setSearchLoading(false);
    }
  }, [role, location, sort]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setSelectedJobId(id);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await fetchStoredJobDetail(id);
      setDetailJob(data);
    } catch (error) {
      setDetailError(error);
      setDetailJob(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedJobId) loadDetail(selectedJobId);
  }, [selectedJobId, loadDetail]);

  const handleCheckFit = (job) => {
    const id = jobId(job);
    if (!id) return;
    safeStoreSelectedJob(job);
    navigate(`/find-jobs/check-fit?job_id=${encodeURIComponent(id)}`);
  };

  return (
    <div className="min-h-screen bg-[#f6efe3] py-8 text-[#22362f] md:py-12" data-testid="stored-job-search-page">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <section className="mb-8" data-testid="stored-job-search-header">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#d9c8aa] bg-white/45 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#6c5d48]" data-testid="stored-job-search-eyebrow">
            <Briefcase className="h-3.5 w-3.5" /> Open jobs
          </span>
          <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#172820] sm:text-5xl" data-testid="stored-job-search-title">Search open jobs first</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[#6f6657] md:text-base" data-testid="stored-job-search-copy">Browse stored jobs anonymously, then check your fit when a role looks worth reviewing.</p>
        </section>

        <section className="jmp-panel mb-6 p-5" data-testid="stored-job-filter-panel">
          {filtersLoading ? (
            <div className="flex min-h-[120px] items-center gap-3 text-sm text-[#6f6657]" data-testid="stored-job-filters-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading filters…</div>
          ) : filtersError ? (
            <ApiNotice title="Filters unavailable" message={safeApiMessage(filtersError)} onRetry={loadFilters} testid="stored-job-filters-error" />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_0.7fr_auto] lg:items-end">
              <label className="space-y-2" htmlFor="stored-job-role-filter" data-testid="stored-job-role-filter-field">
                <span className="text-sm font-medium text-[#22362f]">Role</span>
                <select id="stored-job-role-filter" value={role} onChange={(event) => handleRoleChange(event.target.value)} className="min-h-[44px] w-full rounded-xl border border-[#8b7652] bg-white/70 px-3 text-sm" data-testid="stored-job-role-filter">
                  <option value="">Choose role</option>
                  {availableRoleOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </label>
              <label className="space-y-2" htmlFor="stored-job-location-filter" data-testid="stored-job-location-filter-field">
                <span className="text-sm font-medium text-[#22362f]">Location</span>
                <select id="stored-job-location-filter" value={location} onChange={(event) => handleLocationChange(event.target.value)} className="min-h-[44px] w-full rounded-xl border border-[#8b7652] bg-white/70 px-3 text-sm" data-testid="stored-job-location-filter">
                  <option value="">Choose location</option>
                  {availableLocationOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
              </label>
              <label className="space-y-2" htmlFor="stored-job-sort-filter" data-testid="stored-job-sort-filter-field">
                <span className="text-sm font-medium text-[#22362f]">Sort</span>
                <select id="stored-job-sort-filter" value={sort} onChange={(event) => setSort(event.target.value)} className="min-h-[44px] w-full rounded-xl border border-[#8b7652] bg-white/70 px-3 text-sm" data-testid="stored-job-sort-filter">
                  <option value="relevance">relevance</option>
                  <option value="newest">newest</option>
                </select>
              </label>
              <Button type="button" onClick={() => runSearch(1)} className="btn-primary min-h-[44px]" disabled={searchLoading} data-testid="stored-job-search-submit">
                {searchLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />} Search jobs
              </Button>
            </div>
          )}
          {validationError && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="stored-job-validation-error">{validationError}</p>}
        </section>

        {searchLoading && <div className="rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-5 text-sm text-[#6f6657]" data-testid="stored-job-results-loading"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading jobs…</div>}
        {searchError && <ApiNotice title="Search unavailable" message={safeApiMessage(searchError)} onRetry={() => runSearch(page)} testid="stored-job-search-error" />}
        {!searchLoading && !searchError && searchPayload && jobs.length === 0 && (
          <div className="rounded-2xl border border-[#d8c8ab] bg-[#fff9ee]/80 p-6 text-sm text-[#6f6657]" data-testid="stored-job-empty-state">{emptyMessage(searchPayload) || 'No stored jobs were returned for this selection.'}</div>
        )}
        {!searchLoading && !searchError && thinMessage(searchPayload) && (
          <div className="mb-5 rounded-2xl border border-[#d8c8ab] bg-[#fff4dc] p-4 text-sm text-[#6f5b2f]" data-testid="stored-job-thin-state">{thinMessage(searchPayload)}</div>
        )}

        {!searchLoading && !searchError && jobs.length > 0 && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[0.64fr_0.36fr]" data-testid="stored-job-results-layout">
            <section className="space-y-4" data-testid="stored-job-results-list">
              {jobs.map((job) => {
                const isSelected = jobId(job) === selectedJobId;
                return (
                  <div key={jobId(job)}>
                    <JobCard job={job} selected={isSelected} onSelect={loadDetail} onCheckFit={handleCheckFit} />
                    {isSelected && (
                      <div className="mt-3 lg:hidden" data-testid={`stored-job-inline-detail-${jobId(job)}`}>
                        <JobDetail job={detailJob || job} loading={detailLoading} error={detailError} onRetry={() => loadDetail(selectedJobId)} onCheckFit={handleCheckFit} />
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex flex-col gap-3 border-t border-[#d8c8ab] pt-4 sm:flex-row sm:items-center sm:justify-between" data-testid="stored-job-pagination">
                <Button type="button" variant="outline" disabled={page <= 1 || searchLoading} onClick={() => runSearch(page - 1)} data-testid="stored-job-prev-page">Previous</Button>
                <span className="text-sm text-[#6f6657]" data-testid="stored-job-page-indicator">Page {page} of {pages}</span>
                <Button type="button" variant="outline" disabled={page >= pages || searchLoading} onClick={() => runSearch(page + 1)} data-testid="stored-job-next-page">Next</Button>
              </div>
            </section>
            <div className="hidden lg:block">
              <JobDetail job={detailJob || jobs.find((job) => jobId(job) === selectedJobId)} loading={detailLoading} error={detailError} onRetry={() => loadDetail(selectedJobId)} onCheckFit={handleCheckFit} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
